import React from 'react';
import { Box, Typography, IconButton, Paper, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutline from '@mui/icons-material/PersonOutline';
import TagIcon from '@mui/icons-material/Tag';
import DesktopWindowsOutlined from '@mui/icons-material/DesktopWindowsOutlined';

const AuditLogDetail: React.FC<{ audit: any; onBack: () => void }> = ({ audit, onBack }) => {
    const info = audit || {};

    // try to derive a friendly name from target if available
    const targetName = (info.target_entity || '').toString();


    return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0 17.5px', width: 1100, minHeight: 600, backgroundColor:'#F9FAFB' }}>
            {/* Back link moved inline with title below to allow tighter spacing */}

            <Box sx={{ width: '100%', mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <IconButton size="small" onClick={onBack} sx={{ width: 28, height: 28, p: 0 }}>
                        <ArrowBackIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    </IconButton>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: 13, color: '#6B7280' }}>Back to Audit Log</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl:1 }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: 20, color: '#111827' }}>Audit entry</Typography>
                    <Box sx={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 9999, px: 1.5, py: 0.5 }}>
                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: 11, color: '#60AF97' }}>{info.action || 'CREATE'}</Typography>
                    </Box>
                </Box>
            </Box>

            {/* Metadata card */}
            <Paper sx={{ width: 1040, mt: 3, p: '21px 21px 21px', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '14px' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>Metadata</Typography>
                <Box sx={{ display: 'flex', gap: 6, width: '100%' }}>
                    <Box sx={{ width: 395 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ width: 28, height: 28, background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <AccessTimeIcon sx={{ fontSize: 13, color: '#6B7280' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>Timestamp</Typography>
                                <Typography sx={{ fontSize: 13, color: '#374151' }}>{info.timestamp || '-'}</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ width: 28, height: 28, background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <DesktopWindowsOutlined sx={{ fontSize: 13, color: '#6B7280' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>IP Address</Typography>
                                <Typography sx={{ fontSize: 13, color: '#374151' }}>{info.ip || '192.168.1.42'}</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}> 
                            <Box sx={{ width: 28, height: 28, background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <TagIcon sx={{ fontSize: 13, color: '#6B7280' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>Target entity</Typography>
                                <Typography sx={{ fontSize: 13, color: '#374151' }}>{targetName || '-'}</Typography>
                            </Box>
                        </Box>
                        
                    </Box>

                    <Box sx={{ width: 395 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ width: 28, height: 28, background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PersonOutline sx={{ fontSize: 13, color: '#6B7280' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>Actor</Typography>
                                {/* KAN-147: don't render empty parentheses when email is missing */}
                                <Typography sx={{ fontSize: 13, color: '#374151' }}>{info.actor ? (info.email ? `${info.actor} (${info.email})` : info.actor) : '-'}</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{ width: 28, height: 28, background: '#F3F4F6', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <TagIcon sx={{ fontSize: 13, color: '#6B7280' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>Action type</Typography>
                                <Typography sx={{ fontSize: 13, color: '#374151' }}>{info.action || '-'}</Typography>
                            </Box>
                        </Box>

                        {/* userAgent moved to left column to match design */}
                    </Box>
                </Box>
            </Paper>

            {/* Changes card */}
            <Paper sx={{ width: 1040, mt: 3, p: '21px', minHeight: 260, background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '14px' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>Changes</Typography>
                <Box sx={{ display: 'flex', gap: 4, width: '100%' }}>
                    <Box sx={{ width: 395 }}>
                        <Box sx={{ width: '78.140625px', height: '30.5px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '10px', borderWidth: '1px', display: 'inline-flex', alignItems: 'center', gap: '9px', px: '8px', py: 0.5, mb: 2, opacity: 1, transform: 'rotate(0deg)' }}>
                            <Box sx={{ width: 8, height: 8, background: '#DE606C', borderRadius: 9999 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: '11px', color: '#DE606C' }}>Before</Typography>
                        </Box>

                        <Paper sx={{ border: '1px solid #E5E7EB', borderRadius: 4, p: 2, minHeight: 160 }}>
                            {info.before && Object.keys(info.before).length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {Object.keys(info.before).map((k: string, i: number) => (
                                        <Box key={k}>
                                            {/* KAN-140: compact key column + value takes the remaining width so a
                                                long value (e.g. an S3 photo URL) wraps neatly inside the box instead
                                                of cramming into a half-width column and looking misaligned. */}
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(90px, max-content) 1fr', gap: 2, alignItems: 'start' }}>
                                                <Typography sx={{ fontWeight: 500, color: '#575757', fontSize:'12px', minWidth: 0, overflowWrap: 'break-word' }}>{k}</Typography>
                                                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '12px', lineHeight: '18px', color: '#DE606C', textAlign: 'left', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{typeof info.before[k] === 'object' ? JSON.stringify(info.before[k]) : String(info.before[k])}</Typography>
                                            </Box>
                                            {i < Object.keys(info.before).length - 1 && <Divider sx={{ my: 1 }} />}
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
                                    <Typography sx={{ color: '#9CA3AF', textAlign: 'center', fontSize:'13px' }}>No previous state (create action)</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    <Box sx={{ width: 395 }}>
                        <Box sx={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '10px', display: 'inline-flex', alignItems: 'center', gap: '9px', px: '8px', py: 0.5, mb: 2, width: '70px', height: '30.5px', transform: 'rotate(0deg)', opacity: 1 }}>
                            <Box sx={{ width: 8, height: 8, background: '#60AF97', borderRadius: 1 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: '11px', color: '#60AF97' }}>After</Typography>
                        </Box>

                        <Paper sx={{ border: '1px solid #E5E7EB', borderRadius: 4, p: 2, minHeight: 160 }}>
                            {info.after && Object.keys(info.after).length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {Object.keys(info.after).map((k: string, i: number) => (
                                        <Box key={k}>
                                            {/* KAN-140: compact key column + value takes the remaining width so a
                                                long value (e.g. an S3 photo URL) wraps neatly inside the box instead
                                                of cramming into a half-width column and looking misaligned. */}
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(90px, max-content) 1fr', gap: 2, alignItems: 'start' }}>
                                                <Typography sx={{ fontWeight: 500, color: '#575757', fontSize:'12px', minWidth: 0, overflowWrap: 'break-word' }}>{k}</Typography>
                                                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '12px', lineHeight: '18px', color: '#60AF97', textAlign: 'left', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{typeof info.after[k] === 'object' ? JSON.stringify(info.after[k]) : String(info.after[k])}</Typography>
                                            </Box>
                                            {i < Object.keys(info.after).length - 1 && <Divider sx={{ my: 1 }} />}
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
                                    <Typography sx={{ color: '#9CA3AF', textAlign: 'center', fontSize:'13px' }}>No data found</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default AuditLogDetail;
